import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '@/app/store.js';
import { setCredentials } from './authSlice.js';

/**
 * Attempts to restore the authenticated session on app startup.
 *
 * Since tokens are no longer persisted in localStorage, a page refresh
 * loses the in-memory accessToken. This hook calls /auth/refresh which
 * reads the httpOnly refreshToken cookie and returns a fresh accessToken
 * (+ user profile) so the user stays logged in across reloads.
 */
export function useAuthInit() {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((s: RootState) => s.auth.isAuthenticated);
  const [isLoading, setIsLoading] = useState(!isAuthenticated);

  useEffect(() => {
    if (isAuthenticated) {
      setIsLoading(false);
      return;
    }

    // Try to refresh from httpOnly cookie
    fetch('/api/v1/auth/refresh', {
      method: 'POST',
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.data?.accessToken) {
          dispatch(
            setCredentials({
              user: data.data.user,
              accessToken: data.data.accessToken,
            }),
          );
        }
      })
      .catch(() => {
        // No valid refresh cookie — user is not logged in
      })
      .finally(() => setIsLoading(false));
  }, []);

  return { isLoading };
}
